import { Fragment, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { css } from '@emotion/react';

import { GeopostPopup } from 'components/geopostPopup/GeopostPopup';
import { IQueueItemParam } from '../models/interfaces/IQueueGroup';
import { ITraceHeader, useGetTracesHeaders } from '../api/useTraceHeadersController';

const BYTE_PARAM_NAMES: readonly string[] = [
    'CoordinateX',
    'CoordinateY',
    'Inline',
    'Xline',
    'ByteScalar',
    'Trace',
    'Cdp',
    'ShotPoint',
];

const contentStyle = css({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: 12,
    '& .controls-row': {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        flexShrink: 0,
    },
    '& .loading-box': {
        display: 'flex',
        justifyContent: 'center',
        padding: 32,
    },
    '& .empty-box': {
        display: 'flex',
        justifyContent: 'center',
        padding: 32,
    },
    '& .table-wrapper': {
        border: '1px solid #e0e0e0',
        borderRadius: 4,
        overflow: 'auto',
    },
});

interface IQueueProcessingTraceHeaderPopupProps {
    open: boolean;
    onChangeOpen: (open: boolean) => void;
    filePath: string;
    itemParams: IQueueItemParam[];
}

export function QueueProcessingTraceHeaderPopup({ open, onChangeOpen, filePath, itemParams }: IQueueProcessingTraceHeaderPopupProps) {
    const [startTrace, setStartTrace] = useState(1);
    const [endTrace, setEndTrace] = useState(100);
    const [ignoreZeros, setIgnoreZeros] = useState(true);
    const [traceData, setTraceData] = useState<ITraceHeader[]>([]);
    const [rowSelections, setRowSelections] = useState<Record<string, string>>({});

    const { mutate: fetchHeaders, isLoading } = useGetTracesHeaders();

    const byteValueToParamName = new Map<string, string>(
        BYTE_PARAM_NAMES
            .map(name => {
                const param = itemParams.find(p => p.Param.toLowerCase() === name.toLowerCase());
                return param ? [param.Value, name] as [string, string] : null;
            })
            .filter((entry): entry is [string, string] => entry !== null)
    );

    const handleShowHeaders = () => {
        fetchHeaders(
            { minTrace: startTrace, maxTrace: endTrace, path: filePath, ignoreZeros },
            {
                onSuccess: (data) => {
                    setTraceData(data);

                    const allKeys = Array.from(new Set(data.flatMap(t => Object.keys(t.Headers))));
                    const initialSelections: Record<string, string> = {};

                    for (const key of allKeys) {
                        const startByte = key.split('-')[0];
                        const matchedParam = byteValueToParamName.get(startByte);

                        if (matchedParam !== undefined) {
                            initialSelections[key] = matchedParam;
                        }
                    }

                    setRowSelections(initialSelections);
                },
            }
        );
    };

    const allHeaderKeys = traceData.length > 0
        ? Array.from(new Set(traceData.flatMap(t => Object.keys(t.Headers))))
        : [];

    return (
        <GeopostPopup
            open={open}
            onChangeOpen={onChangeOpen}
            title='Trace Header Inspect'
            width={960}
            height={560}
            resizable={false}
            draggable
            overlay
            padding='16px'
        >
            <Box css={contentStyle}>
                <Box className='controls-row'>
                    <TextField
                        label='Start Trace'
                        type='number'
                        size='small'
                        value={startTrace}
                        onChange={(e) => setStartTrace(Number(e.target.value))}
                        sx={{ width: 120 }}
                    />
                    <TextField
                        label='End Trace'
                        type='number'
                        size='small'
                        value={endTrace}
                        onChange={(e) => setEndTrace(Number(e.target.value))}
                        sx={{ width: 120 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={ignoreZeros}
                                onChange={(e) => setIgnoreZeros(e.target.checked)}
                                size='small'
                            />
                        }
                        label='Ignore Zeros'
                    />
                    <Button
                        variant='contained'
                        size='small'
                        onClick={handleShowHeaders}
                        disabled={isLoading}
                        sx={{ textTransform: 'none' }}
                    >
                        {isLoading ? 'Loading...' : 'Show Headers'}
                    </Button>
                </Box>
                {isLoading && (
                    <Box className='loading-box'>
                        <CircularProgress />
                    </Box>
                )}
                {!isLoading && traceData.length > 0 && (
                    <Fragment>
                        <TableContainer className='table-wrapper'>
                            <Table size='small' stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontWeight: 700,
                                                backgroundColor: '#f5f5f5',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 3,
                                                whiteSpace: 'nowrap',
                                                borderRight: '1px solid #e0e0e0',
                                            }}
                                        >
                                            Header
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 700,
                                                backgroundColor: '#f5f5f5',
                                                whiteSpace: 'nowrap',
                                                borderRight: '1px solid #e0e0e0',
                                            }}
                                        >
                                            Load param
                                        </TableCell>
                                        {traceData.map(trace => (
                                            <TableCell
                                                key={trace.TraceNumber}
                                                sx={{
                                                    fontWeight: 700,
                                                    backgroundColor: '#f5f5f5',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Trace {trace.TraceNumber}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {allHeaderKeys.map(headerKey => {
                                        const startByte = headerKey.split('-')[0];
                                        const isHighlighted = byteValueToParamName.has(startByte);

                                        return (
                                            <TableRow
                                                key={headerKey}
                                                sx={{ backgroundColor: isHighlighted ? '#fff9c4' : 'inherit' }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        position: 'sticky',
                                                        left: 0,
                                                        zIndex: 1,
                                                        backgroundColor: isHighlighted ? '#fff9c4' : '#ffffff',
                                                        whiteSpace: 'nowrap',
                                                        borderRight: '1px solid #e0e0e0',
                                                    }}
                                                >
                                                    {headerKey}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        backgroundColor: isHighlighted ? '#fff9c4' : 'inherit',
                                                        borderRight: '1px solid #e0e0e0',
                                                        padding: '4px 8px',
                                                    }}
                                                >
                                                    <Select
                                                        size='small'
                                                        value={rowSelections[headerKey] ?? ''}
                                                        onChange={(e) => setRowSelections(prev => ({ ...prev, [headerKey]: e.target.value }))}
                                                        displayEmpty
                                                        disabled
                                                        sx={{ fontSize: 12, minWidth: 140 }}
                                                    >
                                                        <MenuItem value=''>
                                                            <em>—</em>
                                                        </MenuItem>
                                                        {BYTE_PARAM_NAMES.map(name => (
                                                            <MenuItem key={name} value={name} sx={{ fontSize: 12 }}>
                                                                {name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                                {traceData.map(trace => (
                                                    <TableCell key={trace.TraceNumber} sx={{ whiteSpace: 'nowrap' }}>
                                                        {trace.Headers[headerKey] ?? '—'}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Fragment>
                )}
                {!isLoading && traceData.length === 0 && (
                    <Box className='empty-box'>
                        <Typography variant='body2' sx={{ color: '#9e9e9e' }}>
                            Configure o intervalo de traces e clique em "Show Headers".
                        </Typography>
                    </Box>
                )}
            </Box>
        </GeopostPopup>
    );
}
